import React, { useEffect } from "react";
import { SaveButton, Toolbar } from "react-admin";
import { useFormContext, useFormState } from "react-hook-form";
import DraftsIcon from "@mui/icons-material/Drafts";
import SendIcon from "@mui/icons-material/Send";

import { HandoverAddress } from "../api/amend";
import { Prefecture } from "../api/prefectures";
import { WholesalerAddress } from "../api/wholesaler_addresses";
import { RegisterHacobellButton } from "./RegisterHacobellButton";
import { findBasePrice } from "../utils/hacobell";

const commonStyles = {
  button: {
    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
    marginRight: "8px",
    px: { xs: 1, sm: 1.5 },
    whiteSpace: "nowrap",
  },
};

export const IndividualDeliveryToolbar = (props: {
  disabledSaveAsDraft: boolean;
  saveAsNotifyDriverInfo: boolean;
  handoverAddress: HandoverAddress | null;
  prefectures: Prefecture[];
  wholesalerAddresses: WholesalerAddress[];
  formTransform: (d: any) => any;
  isSubmitActionRef: React.MutableRefObject<boolean>;
  [key: string]: any;
}) => {
  const {
    formTransform,
    disabledSaveAsDraft,
    saveAsNotifyDriverInfo,
    handoverAddress,
    prefectures,
    wholesalerAddresses,
    isSubmitActionRef,
    ...rest
  } = props;
  const formContext = useFormContext();
  const { setValue, watch } = formContext;
  const { errors, isSubmitting } = useFormState();

  const delivery_type = watch("delivery_type");
  const delivery_address_id = watch("delivery_address_id");
  const hacobell = watch("hacobell");

  useEffect(() => {
    // エラーが出たかつ submit 中ではない（＝バリデーション失敗）ときにリセット
    if (Object.keys(errors).length > 0 && !isSubmitting) {
      isSubmitActionRef.current = false;
    }
  }, [errors, isSubmitting]);

  if (!delivery_type) {
    return null;
  }

  const toAddress = wholesalerAddresses.find((wa) => wa.id === delivery_address_id);
  const fromPref = prefectures.find((p) => p.id === handoverAddress?.prefecture_id);
  const toPref = prefectures.find((p) => p.id === toAddress?.prefecture_id);
  let price = null;
  if (fromPref !== undefined && toPref !== undefined && hacobell && hacobell.preferred_vehicle) {
    price = findBasePrice(fromPref, toPref, hacobell.preferred_vehicle);
  }
  const hasPrice = price !== null;

  switch (delivery_type) {
    case "self_delivery": {
      return (
        <Toolbar {...rest}>
          <SaveButton
            label="ui.individual_deliveries.save_as_draft"
            icon={<DraftsIcon />}
            color="inherit"
            type="button"
            onClick={() => {
              isSubmitActionRef.current = true;
              setValue("status", "draft");
            }}
            transform={formTransform}
            disabled={disabledSaveAsDraft}
            sx={commonStyles.button}
          />
          {!saveAsNotifyDriverInfo && (
            <SaveButton
              label="ui.individual_deliveries.publish"
              icon={<SendIcon />}
              color="primary"
              type="button"
              onClick={() => {
                isSubmitActionRef.current = true;
                setValue("status", "waiting_for_inspection");
              }}
              transform={formTransform}
              sx={commonStyles.button}
            />
          )}
          {saveAsNotifyDriverInfo && (
            <SaveButton
              label="ui.individual_deliveries.save_as_notify_driver_info"
              icon={<SendIcon />}
              color="primary"
              type="button"
              onClick={() => {
                isSubmitActionRef.current = true;
                setValue("status", "waiting_for_inspection");
              }}
              transform={formTransform}
              sx={commonStyles.button}
            />
          )}
        </Toolbar>
      );
    }
    case "hacobell": {
      return (
        <Toolbar {...rest}>
          <SaveButton
            label="ui.individual_deliveries.save_as_draft"
            icon={<DraftsIcon />}
            color="inherit"
            type="button"
            onClick={() => {
              isSubmitActionRef.current = true;
              setValue("status", "draft");
            }}
            transform={formTransform}
            sx={commonStyles.button}
          />
          <RegisterHacobellButton
            disabled={!hasPrice || isSubmitting}
            transform={formTransform}
            isSubmitActionRef={isSubmitActionRef}
          />
        </Toolbar>
      );
    }
    default: {
      return null;
    }
  }
};
